import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import { editSolicitud, createMessage, createCoverage, addComentario } from "../../../slices/thunks";
import { getTasaDolar } from "../../../helpers/backend_helper";
import { pathologies } from "./patologiasCronicas";
import { limpiarDatosSolicitud } from "../../../utils/solicitudDataCleaner";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Label,
  Row,
  Form,
  FormGroup,
  Input,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";

const RegistroMedicamentosCronicos = ({ solicitud }) => {
  const dispatch = useDispatch();
  const [mensaje, setMensaje] = useState("");
  const [mensajeError, setMensajeError] = useState("");
  const [medicamentos, setMedicamentos] = useState("");
  const [bioequivalentes, setBioequivalentes] = useState("");

  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [loadingx, setLoadingx] = useState(false);
  const [procesandoRegistro, setProcesandoRegistro] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [tasaDolar, setTasaDolar] = useState(null);

  const selectLayoutState = (state) => state;
  const pageData = createSelector(selectLayoutState, (state) => ({
    siscotel: state.Siscotel.siscotel,
    error: state.Siscotel.error,
    loading: state.Siscotel.loading,
    errorMsg: state.Siscotel.errorMsg,
    colorMsg: state.Siscotel.colorMsg,
    exito: state.Siscotel.exito,
  }));

  const { siscotel, error, loading, errorMsg, colorMsg, exito } =
    useSelector(pageData);

  // Cargar tasa del dólar al montar el componente
  useEffect(() => {
    const fetchTasaDolar = async () => {
      try {
        const response = await getTasaDolar();
        if (response.status === 200 && response.data) {
          setTasaDolar(response.data);
        }
      } catch (error) {
        console.error("Error al obtener tasa del dólar:", error);
      }
    };

    fetchTasaDolar();
  }, []);

  /**
   * Normaliza el texto separando por comas, punto y coma, o saltos de línea
   * @param {string} texto - Texto a normalizar
   * @returns {array} - Array de items limpios
   */
  const normalizarTexto = (texto) => {
    if (!texto || texto.trim() === '') return [];
    
    // Separar por saltos de línea, comas, o punto y coma
    return texto
      .split(/[\n,;]+/)
      .map(item => item.trim())
      .filter(item => item.length > 0);
  };

  useEffect(() => {
    if (siscotel?.status === 200 && procesandoRegistro) {
      // Preparar el historial de entrega usando normalización
      const nuevaEntrega = {
        fecha: new Date().toISOString(),
        medicamentos: normalizarTexto(medicamentos),
        bioequivalentes: normalizarTexto(bioequivalentes),
      };

      // Preparar el historial actualizado
      const historialActualizado = solicitud.cronico?.historialEntregas
        ? [...solicitud.cronico.historialEntregas, nuevaEntrega]
        : [nuevaEntrega];

      // Usar utilidad centralizada para limpiar datos
      const editarSolicitud = limpiarDatosSolicitud({
        ...solicitud,
        cronico: {
          ...solicitud.cronico,
          historialEntregas: historialActualizado,
        },
      });

      dispatch(editSolicitud(editarSolicitud));
      setLoadingx(false);
      setProcesandoRegistro(false);
      setBioequivalentes("");
      setMedicamentos("");

      toast.success("Medicamentos registrados exitosamente", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  }, [dispatch, siscotel, procesandoRegistro, solicitud, medicamentos, bioequivalentes]);

  /**
   * Abre el modal de confirmación
   */
  const handleOpenConfirmModal = () => {
    // Validar que al menos uno de los campos tenga contenido
    const tieneMedicamentos = medicamentos && medicamentos.trim() !== '';
    const tieneBioequivalentes = bioequivalentes && bioequivalentes.trim() !== '';
    
    if (!tieneMedicamentos && !tieneBioequivalentes) {
      setShowErrorMessage(true);
      return;
    }
    
    setShowErrorMessage(false);
    setShowConfirmModal(true);
  };

  /**
   * Confirma y procesa el registro
   * Primero consulta la cobertura actualizada, luego registra los medicamentos
   */
  const handleConfirmSubmit = async () => {
    setShowConfirmModal(false);
    setLoadingx(true);
    
    try {
      // 1. Primero consultar la cobertura actualizada
      toast.info("🔍 Consultando cobertura actualizada...", {
        position: "top-right",
        autoClose: 2000,
      });

      const cobertura = {
        CedulaTitular: solicitud.titularCedula,
        CedulaSolicitante: solicitud.solicitanteCedula,
        CodigoDiagnostico: solicitud.cronico.patologia,
        CodigoProveedor: solicitud.codigoProveedor,
      };

      const responseCoverage = await dispatch(createCoverage(cobertura));
      
      // Verificar si la consulta de cobertura fue exitosa
      const isSuccess = 
        (responseCoverage && responseCoverage.payload && responseCoverage.payload.status === 200) || 
        (responseCoverage && responseCoverage.status === 200) ||
        (responseCoverage && responseCoverage.type && responseCoverage.type.endsWith('/fulfilled'));
      
      if (isSuccess) {
        const siscotelData = responseCoverage.payload || responseCoverage;
        
        // Obtener el valor de cobertura
        const coverageData = 
          siscotelData.coverage || 
          (siscotelData.payload && siscotelData.payload.coverage) ||
          (siscotelData.data && siscotelData.data.coverage);
        
        if (coverageData) {
          const coverageValue =
            typeof coverageData === "string"
              ? parseFloat(coverageData.replace(" $", ""))
              : typeof coverageData === "number"
              ? coverageData
              : 0;
          
          const nombreCronico =
            pathologies.find((p) => p.codigo === solicitud.cronico.patologia)
              ?.nombre_cronico ?? "Nombre crónico no encontrado";
          
          // Usar utilidad centralizada para limpiar datos
          const solicitudConCobertura = limpiarDatosSolicitud({
            ...solicitud,
            diagnosticoFarmacologico: nombreCronico,
            codigoDiagnosticoFarmacologico: solicitud.cronico.patologia,
            montoCobertura: coverageValue,
            porcentaje_avance: Math.max(solicitud.porcentaje_avance || 0, 20),
          });
          
          await dispatch(editSolicitud(solicitudConCobertura));
          
          toast.success(
            `✅ Cobertura actualizada: ${new Intl.NumberFormat("es-VE", {
              style: "currency",
              currency: "USD",
            }).format(coverageValue)}`,
            {
              position: "top-right",
              autoClose: 3000,
            }
          );
          
          // 2. Ahora proceder con el registro de medicamentos
          setProcesandoRegistro(true);
          handleSubmit();
        } else {
          toast.error("No se pudo obtener el monto de cobertura");
          setLoadingx(false);
        }
      } else {
        // Usar siscotelAnswer que contiene el mensaje real de Siscotel
        if (solicitud.aseguradora === "MyH+") {
          const errorMsg = responseCoverage?.siscotelAnswer || responseCoverage?.payload?.siscotelAnswer || "Error al consultar cobertura";
          toast.error(`Error en MyH+: ${errorMsg}`);
        }
        setLoadingx(false);
      }
    } catch (error) {
      if (solicitud.aseguradora === "MyH+") {
        const errorMsg = error?.message || "Error al consultar cobertura";
        toast.error(`Error en MyH+: ${errorMsg}`);
      }
      setLoadingx(false);
    }
  };

  /**
   * Handles the submission of chronic medication records.
   *
   * This function processes the input medications and bioequivalents, constructs a message
   * in the required format, and dispatches it to the Siscotel system.
   *
   * @function
   * @returns {void}
   */
  const handleSubmit = async () => {
    try {
      // Procesar medicamentos y bioequivalentes usando normalización
      let medicamentosTratados = normalizarTexto(medicamentos);
      let bioequivalentesTratados = normalizarTexto(bioequivalentes);

      // Construir el mensaje según el formato requerido
      let mensajeFinal = "";

      // Primero colocamos todos los medicamentos
      if (medicamentosTratados.length > 0) {
        mensajeFinal = medicamentosTratados.join("|");
      }

      // Luego agregamos los bioequivalentes si existen
      if (bioequivalentesTratados.length > 0) {
        // Si ya hay medicamentos, agregamos un separador
        if (mensajeFinal) {
          mensajeFinal += "|";
        }
        // Agregamos los bioequivalentes con el formato especificado
        mensajeFinal += bioequivalentesTratados
          .map((bio) => `bioequivalente : ${bio}`)
          .join("|");
      }

      const message = {
        IdentificadorEvento: solicitud.identificador,
        Tipo: "RecibirMedicamento",
        Mensaje: mensajeFinal,
        Adjunto: "",
        CodigoProveedor: solicitud.codigoProveedor,
      };

      if (solicitud.aseguradora === "Seguros Mercantil") {
        const comentario = {
          tipo: "Enviado",
          mensaje: mensajeFinal,
          estatus: true,
          adjunto: null,
          creado: null,
        };
        dispatch(addComentario({ identificador: String(solicitud.identificador), comentario }));
      } else {
        await dispatch(createMessage(message));
      }
    } catch (error) {
      if (solicitud.aseguradora === "MyH+") {
        toast.error(`Error en MyH+: ${error.message || "Error al registrar medicamentos"}`);
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const RegistroMedicamentosCronicosTable = ({ solicitud }) => {
    if (!solicitud || !solicitud.cronico.historialEntregas || solicitud.cronico.historialEntregas.length === 0) {
      return (
        <div className="alert alert-info d-flex align-items-center" role="alert">
          <i className="ri-information-line fs-18 me-2"></i>
          <div>No hay información de entregas anteriores.</div>
        </div>
      );
    }
  
    return (
      <div>
        <h6 className="text-muted mb-3">
          <i className="ri-history-line me-1"></i>
          Historial de Entregas
        </h6>
        <div className="table-responsive">
          <table className="table table-bordered table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th scope="col" style={{ width: "15%" }}>
                  <div className="d-flex align-items-center">
                    <i className="ri-calendar-line text-success me-1"></i>
                    Fecha
                  </div>
                </th>
                <th scope="col" style={{ width: "42.5%" }}>
                  <div className="d-flex align-items-center">
                    <i className="ri-medicine-bottle-line text-success me-1"></i>
                    Medicamentos
                  </div>
                </th>
                <th scope="col" style={{ width: "42.5%" }}>
                  <div className="d-flex align-items-center">
                    <i className="ri-capsule-line text-success me-1"></i>
                    Bioequivalentes
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {solicitud.cronico.historialEntregas?.map((entrega, index) => (
                <tr key={index}>
                  <td>
                    <span className="badge bg-success-subtle text-success">
                      {new Date(entrega.fecha).toLocaleDateString('es-ES', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })}
                    </span>
                  </td>
                  <td>
                    {entrega.medicamentos.length > 0 ? (
                      <ul className="list-group list-group-flush">
                        {entrega.medicamentos.map((med, idx) => (
                          <li key={idx} className="list-group-item border-0 px-0 py-1">
                            <i className="ri-medicine-bottle-line text-muted me-1"></i>
                            {med}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span className="text-muted fst-italic">No registrados</span>
                    )}
                  </td>
                  <td>
                    {entrega.bioequivalentes.length > 0 ? (
                      <ul className="list-group list-group-flush">
                        {entrega.bioequivalentes.map((bio, idx) => (
                          <li key={idx} className="list-group-item border-0 px-0 py-1">
                            <i className="ri-capsule-line text-muted me-1"></i>
                            {bio}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span className="text-muted fst-italic">No registrados</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const isValidSolicitud = () => {
    // Verificar si la solicitud está cancelada o entregada
    if (solicitud.estatus === "Cancelado" || solicitud.estatus === "Entregado") return false;
    
    // Verificar si la solicitud es válida (Nueva Solicitud o Virtual y está vigente)
    if (solicitud.estatus !== "Nueva Solicitud" && solicitud.estatus !== "Virtual") return false;

    if (!solicitud.cronico?.fechaVigencia) return false;

    const fechaVigencia = new Date(solicitud.cronico.fechaVigencia);
    const today = new Date();

    // Comparar solo año y mes
    return (
      fechaVigencia.getFullYear() > today.getFullYear() ||
      (fechaVigencia.getFullYear() === today.getFullYear() &&
        fechaVigencia.getMonth() >= today.getMonth())
    );
  };

  const hasEntryThisMonth = () => {
    if (!solicitud.cronico?.historialEntregas?.length) return false;
    if (!isValidSolicitud()) return true; // Si no es válida, consideramos que "ya tiene entrada"

    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();

    return solicitud.cronico.historialEntregas.some((entrega) => {
      const entregaDate = new Date(entrega.fecha);
      return (
        entregaDate.getMonth() === currentMonth &&
        entregaDate.getFullYear() === currentYear
      );
    });
  };

  const getNextAvailableDate = () => {
    const today = new Date();
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    return nextMonth.toLocaleDateString("es-ES", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getStatusMessage = () => {
    if (solicitud.estatus === "Cancelado") {
      return {
        title: "Solicitud cancelada",
        message: "Esta solicitud ha sido cancelada y no permite registrar medicamentos.",
      };
    }

    if (solicitud.estatus === "Entregado") {
      return {
        title: "Solicitud entregada",
        message: "Esta solicitud ya fue entregada y no permite registrar más medicamentos.",
      };
    }

    if (solicitud.estatus !== "Nueva Solicitud" && solicitud.estatus !== "Virtual") {
      return {
        title: "Solicitud no disponible",
        message: "Esta solicitud no está en estado 'Nueva Solicitud' o 'Virtual'.",
      };
    }

    if (!solicitud.cronico?.fechaVigencia) {
      return {
        title: "Solicitud sin fecha de vigencia",
        message: "Esta solicitud no tiene una fecha de vigencia establecida.",
      };
    }

    const fechaVigencia = new Date(solicitud.cronico.fechaVigencia);
    if (fechaVigencia < new Date()) {
      return {
        title: "Solicitud vencida",
        message: `Esta solicitud venció el ${fechaVigencia.toLocaleDateString(
          "es-ES",
          {
            year: "numeric",
            month: "long",
            day: "numeric",
          }
        )}.`,
      };
    }

    if (hasEntryThisMonth()) {
      return {
        title: "Registro mensual completado",
        message: `Ya se ha registrado una entrega de medicamentos para este mes.
                  La próxima entrega estará disponible a partir del ${getNextAvailableDate()}.`,
      };
    }

    return null;
  };

  //PARTE PRINCIPAL
  return (
    <React.Fragment>
  <Col>
    <Card>
      <CardHeader className="bg-soft-success">
        <div className="d-flex align-items-center">
          <div className="flex-shrink-0">
            <i className="ri-capsule-fill text-success fs-22"></i>
          </div>
          <div className="flex-grow-1 ms-3">
            <h5 className="card-title mb-0 fw-semibold">
              Registro de Medicamentos Crónicos
            </h5>
          </div>
        </div>
      </CardHeader>
      <CardBody className="p-4">
        <RegistroMedicamentosCronicosTable solicitud={solicitud} />

        {/* Card de Información de Cobertura */}
        {!(solicitud.montoCobertura > 0) ? (
          <div className="card mt-3" style={{ backgroundColor: '#f0f9ff', border: '1px solid #e0f2fe' }}>
            <div className="card-body py-3">
              <div className="d-flex align-items-center gap-2">
                <div className="avatar-xs">
                  <span className="avatar-title rounded-circle" style={{ backgroundColor: '#3b82f6' }}>
                    <i className="ri-money-dollar-circle-line text-white"></i>
                  </span>
                </div>
                <div>
                  <p className="text-muted mb-0 small">Monto de Cobertura</p>
                  <h5 className="mb-0">
                    <span className="text-warning">Sin cobertura asignada</span>
                  </h5>
                </div>
              </div>
            </div>
          </div>
        ) : solicitud?.aseguradora === "Seguros Mercantil" && tasaDolar?.precio ? (
          <div className="card mt-3" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
            <div className="card-body py-3">
              <div className="d-flex align-items-center gap-2">
                <div className="avatar-xs">
                  <span className="avatar-title rounded-circle bg-success">
                    <i className="ri-money-dollar-circle-line text-white"></i>
                  </span>
                </div>
                <div>
                  <p className="text-muted mb-0 small">Monto de Cobertura</p>
                  <h5 className="mb-0">
                    ${solicitud.montoCobertura} USD{" "}
                    <span className="text-muted fs-6">
                      ({new Intl.NumberFormat("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(solicitud.montoCobertura * tasaDolar.precio)} Bs)
                    </span>
                  </h5>
                  <small className="text-muted">
                    Tasa: {tasaDolar.precio.toFixed(2)} Bs/USD
                    {tasaDolar.ultima_actualizacion && (
                      <span className="badge bg-soft-success text-success ms-1">{tasaDolar.ultima_actualizacion}</span>
                    )}
                  </small>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        <div className="mt-4">
          {getStatusMessage() && (
            <div
              className={`alert ${
                !isValidSolicitud() ? "alert-warning" : "alert-info"
              } mt-4`}
              role="alert"
            >
              <div className="d-flex">
                <div className="flex-shrink-0">
                  <i className={`ri-${!isValidSolicitud() ? 'alert' : 'information'}-line fs-18 me-2`}></i>
                </div>
                <div className="flex-grow-1">
                  <h6 className="mb-1">{getStatusMessage().title}</h6>
                  <p className="mb-0">{getStatusMessage().message}</p>
                </div>
              </div>
            </div>
          )}

          {isValidSolicitud() && !hasEntryThisMonth() && (
            <div className="mt-4 p-3 border border-light rounded bg-light">
              {showErrorMessage && (
                <div className="alert alert-danger" role="alert">
                  <i className="ri-error-warning-line me-2"></i>
                  Por favor ingrese al menos un medicamento o bioequivalente
                </div>
              )}
              
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label for="medicamentos" className="form-label fw-medium">
                      <i className="ri-medicine-bottle-line text-success me-1"></i>
                      Medicamentos
                    </Label>
                    <Input
                      type="textarea"
                      className="form-control border-success-subtle"
                      id="medicamentos"
                      rows="4"
                      value={medicamentos}
                      onChange={(e) => setMedicamentos(e.target.value)}
                      placeholder="Ingrese cada medicamento en una línea separada"
                    />
                    <small className="form-text text-muted">
                      <i className="ri-information-line me-1"></i>
                      <strong>Tip:</strong> Puede separar medicamentos por líneas, comas (,) o punto y coma (;). Ejemplo: "Metformina 850mg, Losartán 50mg"
                    </small>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="bioequivalentes" className="form-label fw-medium">
                      <i className="ri-capsule-line text-success me-1"></i>
                      Bioequivalentes
                    </Label>
                    <Input
                      type="textarea"
                      className="form-control border-success-subtle"
                      id="bioequivalentes"
                      rows="4"
                      value={bioequivalentes}
                      onChange={(e) => setBioequivalentes(e.target.value)}
                      placeholder="Ingrese cada bioequivalente en una línea separada"
                    />
                    <small className="form-text text-muted">
                      <i className="ri-information-line me-1"></i>
                      <strong>Tip:</strong> Puede separar bioequivalentes por líneas, comas (,) o punto y coma (;).
                    </small>
                  </FormGroup>
                </Col>
              </Row>
            </div>
          )}

          <Row className="mt-3">
            <Col>
              <div className="mb-3 mt-4 text-end">
                {isValidSolicitud() && !hasEntryThisMonth() && (
                  <button
                    type="button"
                    className="btn btn-success btn-lg px-4"
                    disabled={loadingx}
                    onClick={handleOpenConfirmModal}
                  >
                    {loadingx ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Registrando...
                      </>
                    ) : (
                      <>
                        <i className="ri-save-line align-middle fs-16 me-2"></i>
                        Registrar Medicamentos
                      </>
                    )}
                  </button>
                )}
              </div>
            </Col>
          </Row>
        </div>
      </CardBody>
    </Card>
  </Col>

  {/* Modal de Confirmación */}
  <Modal isOpen={showConfirmModal} toggle={() => setShowConfirmModal(false)} centered size="lg">
    <ModalHeader toggle={() => setShowConfirmModal(false)} className="bg-light border-bottom">
      <div className="d-flex align-items-center">
        <i className="ri-check-double-line text-success fs-3 me-2"></i>
        <h5 className="modal-title mb-0">Confirmar Registro de Medicamentos</h5>
      </div>
    </ModalHeader>
    <ModalBody className="p-4">
      <div className="alert alert-info mb-3">
        <i className="ri-information-line me-2"></i>
        Por favor revise los medicamentos antes de confirmar el registro.
      </div>

      <Row>
        {normalizarTexto(medicamentos).length > 0 && (
          <Col md={6}>
            <h6 className="text-success mb-3">
              <i className="ri-medicine-bottle-line me-1"></i>
              Medicamentos ({normalizarTexto(medicamentos).length})
            </h6>
            <ul className="list-group list-group-flush">
              {normalizarTexto(medicamentos).map((med, idx) => (
                <li key={idx} className="list-group-item border-0 px-0 py-2">
                  <i className="ri-checkbox-circle-line text-success me-2"></i>
                  {med}
                </li>
              ))}
            </ul>
          </Col>
        )}

        {normalizarTexto(bioequivalentes).length > 0 && (
          <Col md={6}>
            <h6 className="text-success mb-3">
              <i className="ri-capsule-line me-1"></i>
              Bioequivalentes ({normalizarTexto(bioequivalentes).length})
            </h6>
            <ul className="list-group list-group-flush">
              {normalizarTexto(bioequivalentes).map((bio, idx) => (
                <li key={idx} className="list-group-item border-0 px-0 py-2">
                  <i className="ri-checkbox-circle-line text-success me-2"></i>
                  {bio}
                </li>
              ))}
            </ul>
          </Col>
        )}
      </Row>

      <div className="alert alert-info mt-3 mb-0">
        <i className="ri-information-line me-2"></i>
        <strong>Proceso automático:</strong>
        <ol className="mb-0 mt-2 ps-3">
          <li>Se consultará la cobertura actualizada</li>
          <li>Se registrarán los medicamentos</li>
          <li>Se enviará la información al seguro</li>
        </ol>
      </div>
    </ModalBody>
    <ModalFooter className="border-top bg-light p-3">
      <Button
        color="light"
        onClick={() => setShowConfirmModal(false)}
        disabled={loadingx}
      >
        <i className="ri-close-line align-bottom me-1"></i>
        Cancelar
      </Button>
      <Button
        color="success"
        onClick={handleConfirmSubmit}
        disabled={loadingx}
      >
        <i className="ri-check-line align-bottom me-1"></i>
        Sí, Confirmar Registro
      </Button>
    </ModalFooter>
  </Modal>
</React.Fragment>
  );
};

export default RegistroMedicamentosCronicos;
